import { EditorReactComponent } from '@wix/component-protocol/schema';
import { EditorElementBuilder } from '@wix/component-protocol/builder';
import {
  BuilderComponent,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { IProcessManifestProps } from '../../lifecycle';

import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';

import { AbstractComponentFlow } from '../AbstractComponentFlow';
import {
  ManifestComponentExtensionType,
  ManifestStore,
  PanelStore,
} from '../../stores';
import { ResolvedManifestPersistentCache } from './ResolvedManifestPersistentCache';
import { COMPONENT_PROTOCOL_VERSION } from './componentProtocolVersion';
import { EditorReactComponentEnricher } from './EditorReactComponentEnricher';
import type { IWixCLIIntegrationAPI } from '../../types';
import type { ManifestServiceFlags } from '../../ManifestServiceFlags';
import {
  RUNTIME_LABEL_MAPPING_VERSION,
  RuntimeLabelsService,
} from '../../runtime-labels';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

export type EditorReactComponentFlowConfig = {
  protocolVersion?: string;
};

export class EditorReactComponentFlow extends AbstractComponentFlow<ManifestStore> {
  static extensionType = ManifestComponentExtensionType.BUILDER as const;
  public readonly type = EditorReactComponentFlow.extensionType;

  readonly #manifestEnricher: EditorReactComponentEnricher;
  readonly #persistentCache: ResolvedManifestPersistentCache;
  readonly #runtimeResolutionEnabled: boolean;

  constructor(
    dm: DocumentServicesObject,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestStore: ManifestStore,
    panelStore: PanelStore,
    wixCLIIntegrationAPI: IWixCLIIntegrationAPI,
    manifestServiceFlags: ManifestServiceFlags,
    config: EditorReactComponentFlowConfig = {},
    private readonly runtimeLabelsService?: RuntimeLabelsService,
  ) {
    super(
      dm,
      editorPointerAPI,
      manifestStore,
      panelStore,
      wixCLIIntegrationAPI,
      manifestServiceFlags,
    );

    const { protocolVersion = COMPONENT_PROTOCOL_VERSION } = config;
    this.#runtimeResolutionEnabled =
      this.manifestServiceFlags?.isRuntimeManifestResolutionEnabled ?? false;
    this.#persistentCache = new ResolvedManifestPersistentCache(
      this.manifestServiceFlags?.isPersistentResolvedManifestCacheEnabled ??
        false,
    );
    const isSpecEnabled = this.manifestServiceFlags
      .isComponentSpecFilteringEnabled
      ? (spec: string) => this.manifestServiceFlags.isSpecEnabled(spec)
      : undefined;

    this.#manifestEnricher = new EditorReactComponentEnricher(
      manifestStore,
      this.clientSpecMapService,
      this.#persistentCache,
      protocolVersion,
      isSpecEnabled,
    );
  }

  override async invalidateCache(): Promise<void> {
    this.runtimeLabelsService?.invalidateCache();
    await this.#persistentCache.clear();
  }

  async loadManifest({
    appDataComponent,
    isPendingApplication,
  }: IProcessManifestProps) {
    const builderComponent = appDataComponent as BuilderComponent;
    const manifest = builderComponent.data as EditorReactComponent;
    const compType = manifest.type;

    if (!compType) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestComponentTypeMissing,
      ).withComponentType(
        (builderComponent as any).appComponent?.id ||
          (builderComponent as any).id ||
          'unknown_id',
      );
    }

    if (!this.#runtimeResolutionEnabled) {
      const runtimeManifest = await this.#completeRuntimeLabels(
        compType,
        manifest,
      );

      this.manifestStore.setManifest(
        compType,
        ManifestComponentExtensionType.BUILDER,
        runtimeManifest,
        null,
        isPendingApplication,
      );

      return;
    }

    const language = this.dm.generalInfo.getLanguage() || 'en';
    const cacheContext = this.manifestServiceFlags
      .isRuntimeManifestLabelsEnabled
      ? {
          language,
          labelMappingVersion: RUNTIME_LABEL_MAPPING_VERSION,
        }
      : {};
    const enrichedManifests = await this.#manifestEnricher.enrich(
      builderComponent,
      compType,
      {
        cacheContext,
        completeManifest: (type, enrichedManifest) =>
          this.#completeRuntimeLabelsForCache(type, enrichedManifest, language),
      },
    );

    for (const [type, runtimeManifest] of Object.entries(enrichedManifests)) {
      this.manifestStore.setManifest(
        type,
        ManifestComponentExtensionType.BUILDER,
        runtimeManifest,
        null,
        isPendingApplication,
      );
    }
  }

  async loadPanels({ compTypes }: IProcessManifestProps) {
    const compType = compTypes[0];

    const manifest = this.manifestStore.getManifest(compType);

    if (!manifest || !manifest.editorElement) {
      return;
    }

    const appData = this.clientSpecMapService.getAppData(compType)!;

    const panels = await this._loadPanels(manifest.editorElement, appData);
    Object.keys(panels).forEach((actionName) => {
      this.panelStore.setPanel(compType, actionName, panels[actionName]);
    });

    const tree = this.manifestStructureService.getChildrenElementManifests(
      manifest.editorElement,
    );

    await Promise.all(
      Object.keys(tree).map(async (innerPath) => {
        const editorElement = new EditorElementBuilder(
          manifest.editorElement,
        ).getElementByPath(innerPath)!;
        const _panels = await this._loadPanels(editorElement, appData);

        Object.keys(_panels).forEach((actionName) => {
          this.panelStore.setPanel(
            PanelStore.getPanelId(compType, innerPath.split('.')),
            actionName,
            _panels[actionName],
          );
        });
      }),
    );
  }

  async #completeRuntimeLabels(
    componentType: string,
    manifest: EditorReactComponent,
    language = this.dm.generalInfo.getLanguage() || 'en',
  ) {
    if (!this.runtimeLabelsService) {
      return manifest;
    }

    return this.runtimeLabelsService.completeLabels({
      componentType,
      language,
      manifest,
    });
  }

  async #completeRuntimeLabelsForCache(
    componentType: string,
    manifest: EditorReactComponent,
    language: string,
  ) {
    if (!this.runtimeLabelsService) {
      return { manifest, cacheable: true };
    }

    return this.runtimeLabelsService.completeLabelsForCache({
      componentType,
      language,
      manifest,
    });
  }
}
